import type { Board } from 'jsxgraph';
import type { TransitionProfile } from '../../../model/widget-action.model';
import { DEFAULT_TRANSITIONS } from '../jsxgraph.model';
import { getEasingFunction } from './action-executor';

/**
 * Context required for bounding box animation.
 */
export interface BoundingBoxAnimationContext {
  board: Board | null;
  isAnimating: boolean;
  isDestroyed: boolean;
  setAnimating: (value: boolean) => void;
}

/**
 * Animate board bounding box change using TransitionProfile.
 */
export function animateBoundingBox(
  ctx: BoundingBoxAnimationContext,
  bbox: [number, number, number, number],
  transition?: TransitionProfile,
): Promise<void> {
  return new Promise((resolve) => {
    if (!ctx.board || ctx.isAnimating) {
      resolve();
      return;
    }

    const profile = transition ?? DEFAULT_TRANSITIONS['default'];

    if (profile.type === 'none' || profile.duration === 0) {
      ctx.board.setBoundingBox(bbox, false);
      resolve();
      return;
    }

    const currentBbox = ctx.board.getBoundingBox();
    const threshold = 0.01;
    const isSame =
      Math.abs(currentBbox[0] - bbox[0]) < threshold &&
      Math.abs(currentBbox[1] - bbox[1]) < threshold &&
      Math.abs(currentBbox[2] - bbox[2]) < threshold &&
      Math.abs(currentBbox[3] - bbox[3]) < threshold;

    if (isSame) {
      resolve();
      return;
    }

    ctx.setAnimating(true);
    const startBbox: [number, number, number, number] = [
      currentBbox[0],
      currentBbox[1],
      currentBbox[2],
      currentBbox[3],
    ];

    const steps = Math.max(1, Math.round(profile.duration / 16));
    const stepDuration = profile.duration / steps;
    const easingFn = getEasingFunction(profile.easing);
    let step = 0;

    const interpolate = (start: number, end: number, progress: number): number => {
      const t = easingFn(progress);
      return start + (end - start) * t;
    };

    const animateStep = () => {
      if (!ctx.board || ctx.isDestroyed) {
        ctx.setAnimating(false);
        resolve();
        return;
      }

      step++;
      const progress = step / steps;

      const newBbox: [number, number, number, number] = [
        interpolate(startBbox[0], bbox[0], progress),
        interpolate(startBbox[1], bbox[1], progress),
        interpolate(startBbox[2], bbox[2], progress),
        interpolate(startBbox[3], bbox[3], progress),
      ];

      ctx.board.setBoundingBox(newBbox, false);

      if (step < steps) {
        setTimeout(animateStep, stepDuration);
      } else {
        ctx.setAnimating(false);
        resolve();
      }
    };

    animateStep();
  });
}

export function resolveTransition(
  transition: string | Partial<TransitionProfile> | undefined,
  instanceTransitions: Record<string, TransitionProfile>,
): TransitionProfile | undefined {
  if (!transition) {
    return DEFAULT_TRANSITIONS['default'];
  }

  if (typeof transition === 'string') {
    return instanceTransitions[transition] || DEFAULT_TRANSITIONS[transition] || DEFAULT_TRANSITIONS['default'];
  }

  return { ...DEFAULT_TRANSITIONS['default'], ...transition };
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function scaleBoundingBox(
  bbox: [number, number, number, number],
  factor: number,
): [number, number, number, number] {
  const centerX = (bbox[0] + bbox[2]) / 2;
  const centerY = (bbox[1] + bbox[3]) / 2;
  const halfWidth = ((bbox[2] - bbox[0]) / 2) * factor;
  const halfHeight = ((bbox[1] - bbox[3]) / 2) * factor;

  return [centerX - halfWidth, centerY + halfHeight, centerX + halfWidth, centerY - halfHeight];
}
